'use client';
import React, { useState } from 'react';
import Topbar from '@/components/layout/Topbar';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  service: string;
  message: string;
}

export default function ContactPage() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
    }, 1500);
  };

  const contactInfo = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      ),
      title: 'Phone',
      lines: ['(+91) 901 583 8496 — India', '+1 (220) 200 3821 — USA'],
      links: ['tel:+919015838496', 'tel:+12202003821'],
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      title: 'Email',
      lines: ['info@bigbraintechnology.com'],
      links: ['mailto:info@bigbraintechnology.com'],
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      title: 'Offices',
      lines: ['🇮🇳 India Operations — Headquarters', '🇺🇸 USA Operations — North America'],
      links: [],
    },
  ];

  return (
    <div className="min-h-screen">
      <Topbar />
      <Header />

      {/* Page Hero */}
      <section className="page-hero-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white font-poppins leading-tight mb-4">
              Contact Us
            </h1>
            <p className="text-white/70 font-lato text-lg leading-relaxed">
              Ready to grow with AI Digital Marketing or find top IT talent in India & USA? Get in touch with our team for a free consultation.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="bbt-section-title-left mb-6">Get In Touch</h2>
              <p className="text-gray-600 font-poppins text-base leading-relaxed mb-8">
                Whether you need AI digital marketing support, IT talent in India or USA, or want to build an offshore team — we&apos;re here to help. Drop us a line and we&apos;ll get back to you right away.
              </p>

              <div className="space-y-6">
                {contactInfo.map((info, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary flex-shrink-0">
                      {info.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-text-main font-lato text-sm uppercase tracking-wide mb-1">{info.title}</h4>
                      {info.lines.map((line, li) => (
                        info.links[li] ? (
                          <a key={li} href={info.links[li]} className="block text-gray-600 font-poppins text-sm hover:text-primary transition-colors">{line}</a>
                        ) : (
                          <p key={li} className="text-gray-600 font-poppins text-sm">{line}</p>
                        )
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Services Quick Links */}
              <div className="mt-10 p-6 bg-secondary-light rounded-lg">
                <h4 className="font-bold text-text-main font-lato text-sm uppercase tracking-wide mb-4">Our Services</h4>
                <div className="space-y-2">
                  {[
                    { label: 'AI Digital Marketing & Growth', href: '/digital-marketing' },
                    { label: 'IT Staffing — India & USA', href: '/it-staffing' },
                    { label: 'AI/ML Talent Acquisition', href: '/it-staffing#ai-talent' },
                    { label: 'Build Offshore Teams', href: '/offshore-teams' },
                    { label: 'AI & Technology Services', href: '/offshore-teams#technology' },
                  ].map((link, i) => (
                    <a key={i} href={link.href} className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary transition-colors font-lato">
                      <svg className="w-3 h-3 text-primary" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                      {link.label}
                    </a>
                  ))}
                </div>
              </div>

              {/* Social Links */}
              <div className="mt-8">
                <p className="font-bold text-text-main font-lato text-sm uppercase tracking-wide mb-4">Follow Us</p>
                <div className="flex items-center gap-3">
                  {[
                    { href: 'https://www.facebook.com/BigBrain-Technology-114032730304517/', label: 'Facebook', icon: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg> },
                    { href: 'https://www.linkedin.com/company/big-brain-technology', label: 'LinkedIn', icon: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg> },
                    { href: 'https://wa.me/917004457736', label: 'WhatsApp', icon: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg> },
                    { href: 'https://www.instagram.com/bigbraintechnology/', label: 'Instagram', icon: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg> },
                  ].map((social, i) => (
                    <a key={i} href={social.href} target="_blank" rel="noopener noreferrer" aria-label={social.label} className="bbt-social-icon bg-gray-100 border-gray-200 text-gray-600">
                      {social.icon}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white border border-border-light rounded-lg p-8 shadow-card">
                <h2 className="bbt-section-title-left mb-2">Send Us a Message</h2>
                <div className="w-10 h-1 bg-primary mb-6"></div>

                {submitted ? (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-2xl font-bold text-text-main font-poppins mb-3">Message Sent!</h3>
                    <p className="text-gray-600 font-poppins">Thank you for reaching out. Our team will get back to you within 24 hours.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-text-main font-lato mb-2 uppercase tracking-wide">Your Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="John Smith"
                          className="w-full border border-border-light rounded px-4 py-3 text-text-main font-poppins text-sm focus:outline-none focus:border-primary transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-text-main font-lato mb-2 uppercase tracking-wide">Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="john@company.com"
                          className="w-full border border-border-light rounded px-4 py-3 text-text-main font-poppins text-sm focus:outline-none focus:border-primary transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-text-main font-lato mb-2 uppercase tracking-wide">Phone Number</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+1 (555) 000-0000"
                          className="w-full border border-border-light rounded px-4 py-3 text-text-main font-poppins text-sm focus:outline-none focus:border-primary transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-text-main font-lato mb-2 uppercase tracking-wide">Company Name</label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Your Company"
                          className="w-full border border-border-light rounded px-4 py-3 text-text-main font-poppins text-sm focus:outline-none focus:border-primary transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-text-main font-lato mb-2 uppercase tracking-wide">Service of Interest *</label>
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        required
                        className="w-full border border-border-light rounded px-4 py-3 text-text-main font-poppins text-sm focus:outline-none focus:border-primary transition-colors bg-white"
                      >
                        <option value="">Select a service...</option>
                        <option value="ai-digital-marketing">AI Digital Marketing</option>
                        <option value="seo-search">SEO & Search Marketing</option>
                        <option value="ppc-ads">PPC & Paid Advertising</option>
                        <option value="social-media">Social Media Marketing</option>
                        <option value="it-staffing-india">IT Staffing — India</option>
                        <option value="it-staffing-usa">IT Staffing — USA</option>
                        <option value="ai-ml-talent">AI/ML Talent Acquisition</option>
                        <option value="offshore-teams">Build Offshore Teams</option>
                        <option value="technology-services">AI & Technology Services</option>
                        <option value="general">General Inquiry</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-text-main font-lato mb-2 uppercase tracking-wide">Message *</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        placeholder="Tell us about your project, requirements, or questions..."
                        className="w-full border border-border-light rounded px-4 py-3 text-text-main font-poppins text-sm focus:outline-none focus:border-primary transition-colors resize-none"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="bbt-btn-primary w-full text-center"
                    >
                      {loading ? 'Sending...' : 'SEND MESSAGE'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Contact BBT */}
      <section className="py-16 bg-secondary-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="bbt-section-title">WHY WORK WITH BBT?</h2>
            <div className="w-16 h-1 bg-primary mx-auto mt-3"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: '🤖', title: 'AI Digital Marketing', desc: 'AI-powered campaigns that deliver 400%+ traffic growth and 6x ROI on ad spend. Smarter marketing for better results.' },
              { icon: '🇮🇳🇺🇸', title: 'India & USA Staffing', desc: 'Access top IT talent across India and the USA — from AI/ML engineers to full-stack developers. 48-hour CV delivery.' },
              { icon: '🌍', title: 'Offshore Teams', desc: 'Build dedicated teams in India that work as an extension of your business. Up to 60% cost savings vs local hiring.' }
            ]?.map((item, i) =>
            <div key={i} className="bbt-value-card animate-on-scroll" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="text-3xl mb-3">{item?.icon}</div>
                <h4 className="font-bold text-text-main font-lato text-sm uppercase tracking-wide mb-3">{item?.title}</h4>
                <p className="text-gray-600 text-sm font-poppins leading-relaxed">{item?.desc}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
